import React, { DispatchWithoutAction, useEffect } from "react";
import { IComponentParent, TriggeredHook } from "sonobello.utilities.react";

import { AppContextProps } from "../../../../AppContext";
import { Step } from "../../../../types/Step";
import { ISomethingWentWrongRouter, IThankYouViewRouter } from "../../../Routing/Types/IRouter";
import IReservation from "../../../Types/IReservation";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import { IConfirmedAppointmentResponse } from "../Hooks/UseConfirmAppointment";
import { IUseGetCalendarEventRequest } from "../Hooks/UseGetCalendarEventUrl";
import { ISchedulingStepWrapperConfig, ISchedulingStepWrapperProps } from "./SchedulingStepWrapper";

export interface ISchedulingConfirmStepWrapperProps extends Omit<ISchedulingStepWrapperProps, "router"> {
  /** Router for error and thank-you navigation. */
  router: IThankYouViewRouter & ISomethingWentWrongRouter;
  /** The confirmed reservation to be booked. Non-null when a slot has been reserved. */
  reservation: IReservation | null;
  /** @see {@link AppContextProps.patient} */
  patient: AppContextProps["patient"];
  /** @see {@link AppContextProps.contactId} */
  contactId: AppContextProps["contactId"];
}

export interface ISchedulingConfirmStepWrapperExperimentProps {
  /** Dispatches that the appointment has been successfully booked. */
  recordBookedAppointment: DispatchWithoutAction;
}

export interface ISchedulingConfirmStepWrapperConfig extends ISchedulingStepWrapperConfig {
  /** Dispatches the confirmed appointment details. */
  useConfirmAppointment: TriggeredHook<IConfirmedAppointmentResponse, IReservation>;
  /** Obtains the calendar event url for the given appointment. */
  useGetCalendarEventUrl: TriggeredHook<string, IUseGetCalendarEventRequest>;
}

/** A step wrapper that combines slot reservation and appointment confirmation into a single scheduling step.
 * Used in the no-confirmation flow variant where the user books directly from the scheduling page.
 */
const SchedulingConfirmStepWrapper: React.FC<
  ISchedulingConfirmStepWrapperProps &
    ISchedulingConfirmStepWrapperExperimentProps &
    ISchedulingConfirmStepWrapperConfig &
    IStepWrapperButtonProps &
    IComponentParent
> = ({
  router,
  value,
  onChange,
  setStep,
  useCreateReservation,
  Scheduler,
  patient,
  contactId,
  reservation,
  recordBookedAppointment,
  useConfirmAppointment,
  useGetCalendarEventUrl,
  ...rest
}) => {
  const { execute: postReservation, error: reservationError, result: reservationResult } = useCreateReservation();
  useEffect(() => {
    if (reservationError) router.goToSomethingWentWrongView();
  }, [reservationError, router]);
  useEffect(() => {
    if (reservationResult) onChange(reservationResult);
  }, [reservationResult, onChange]);

  const {
    result: confirmedAppointment,
    execute: confirmAppointment,
    error: confirmError,
    isLoading: isConfirmLoading
  } = useConfirmAppointment();

  const {
    result: calendarEventUrl,
    execute: getCalendarEventUrl,
    isLoading: isCalendarEventUrlLoading,
    error: calendarEventUrlError
  } = useGetCalendarEventUrl();

  useEffect(() => {
    if (confirmedAppointment && (calendarEventUrl || calendarEventUrlError))
      router.goToThankYouView({
        appointmentInfo: {
          center: reservation!.center,
          startTimeUtc: confirmedAppointment.appointment.startTimeUtc,
          endTimeUtc: confirmedAppointment.appointment.endTimeUtc
        },
        calendarEventUrl: calendarEventUrl || null
      });
  }, [confirmedAppointment, calendarEventUrl, calendarEventUrlError, router, reservation]);

  useEffect(() => {
    if (confirmError) router.goToSomethingWentWrongView();
  }, [confirmError, router]);

  useEffect(() => {
    if (confirmedAppointment) {
      recordBookedAppointment();
      getCalendarEventUrl({ patientId: patient.id, contactId, appointmentId: confirmedAppointment.appointment.id });
    }
  }, [confirmedAppointment, recordBookedAppointment, getCalendarEventUrl, patient, contactId]);

  const goToCenterSelection = () => {
    setStep(Step.centerSelection);
  };

  return (
    <StepWrapper
      {...rest}
      disabled={!value}
      nextButtonLabel="Confirm Appointment"
      nextButtonLoading={isConfirmLoading || isCalendarEventUrlLoading}
      onNext={() => confirmAppointment(reservation!)}
    >
      <Scheduler value={value} onChange={postReservation} goToCenterSelection={goToCenterSelection} />
    </StepWrapper>
  );
};

export default SchedulingConfirmStepWrapper;
